import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FONT_MONO, FONT_SERIF } from './landingData';
import { TemplateCard, TplArrow, type TplCard } from './TemplateCard';

export type { TplCard };

interface Props {
    templates: TplCard[];
    loading: boolean;
    busyId: string | null;
    onUse: (id: string) => void;
    /** Jump to one template: switch to "All", page to it, scroll it into view and ring it briefly. `nonce` makes repeat clicks fire again. */
    focus?: { id: string; nonce: number } | null;
}

const PAGE = 3;

export const TemplatesSection: React.FC<Props> = ({ templates, loading, busyId, onUse, focus = null }) => {
    const [cat, setCat] = useState('All');
    const [page, setPage] = useState(0);
    const [ringId, setRingId] = useState<string | null>(null);
    const handled = useRef<number>(0);

    useEffect(() => {
        if (!focus || focus.nonce === handled.current) return;
        const idx = templates.findIndex((t) => t.id === focus.id);
        if (idx < 0) {
            // Catalog still loading or the id is gone: at least land on the section.
            if (!loading) handled.current = focus.nonce;
            document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        handled.current = focus.nonce;
        setCat('All');
        setPage(Math.floor(idx / PAGE));
        setRingId(focus.id);
        const t1 = setTimeout(() => document.querySelector(`[data-tpl-id="${focus.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 40);
        const t2 = setTimeout(() => setRingId(null), 3000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [focus, templates, loading]);

    const cats = useMemo(() => ['All', ...Array.from(new Set(templates.map((t) => t.tag).filter(Boolean)))], [templates]);
    const filtered = cat === 'All' ? templates : templates.filter((t) => t.tag === cat);
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
    const tplPage = Math.min(page, pages - 1);
    const start = tplPage * PAGE;
    const shown = filtered.slice(start, start + PAGE);
    const canPrev = tplPage > 0;
    const canNext = tplPage < pages - 1;
    // "See all" keeps the category only when it has more than fits here.
    const byCat = cat !== 'All' && filtered.length > PAGE;

    const navBtn = (enabled: boolean): React.CSSProperties => ({ width: 38, height: 38, border: '1px solid var(--hv-line-2)', borderRadius: 999, background: 'var(--hv-card)', color: enabled ? 'var(--hv-ink)' : 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: enabled ? 'pointer' : 'default' });

    return (
        <section data-screen-label="Templates" id="templates" className="hv-band" style={{ scrollMarginTop: 88, position: 'relative', padding: '88px 0', background: 'var(--hv-page)', borderTop: '1px solid var(--hv-line)', borderBottom: '1px solid var(--hv-line)' }}>
            <div className="hv-shell" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 28px' }}>
                <div className="hv-row-wrap" style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 30 }}>
                    <div>
                        <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.18em', color: 'var(--brand,#13422C)' }}>TEMPLATE LIBRARY</div>
                        <h2 className="hv-h2" style={{ margin: '14px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 46, lineHeight: 1.04, letterSpacing: '-0.02em' }}>Start from something <span style={{ fontStyle: 'italic' }}>that already works</span></h2>
                    </div>
                    <div style={{ flex: 1 }} />
                    <div className="hv-tpl-nav" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.1em', color: 'var(--hv-body)' }}>{filtered.length ? `${tplPage + 1} / ${pages}` : '0 / 0'}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <button type="button" onClick={() => setPage(Math.max(0, tplPage - 1))} aria-label="Previous templates" className="hv-h-brand-bd" style={navBtn(canPrev)}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
                            </button>
                            <button type="button" onClick={() => setPage(Math.min(pages - 1, tplPage + 1))} aria-label="Next templates" className="hv-h-brand-bd" style={navBtn(canNext)}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 26, flexWrap: 'wrap' }}>
                    {cats.map((c) => {
                        const active = c === cat;
                        return (
                            <button key={c} type="button" onClick={() => { setCat(c); setPage(0); }} className="hv-h-brand-bd" style={{ height: 36, padding: '0 16px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? 'var(--hv-ink)' : 'var(--hv-line)'}`, background: active ? 'var(--hv-ink)' : 'var(--hv-card)', color: active ? 'var(--hv-page)' : 'var(--hv-body)' }}>{c}</button>
                        );
                    })}
                </div>

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0', fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.16em', color: 'var(--hv-muted)' }}>LOADING TEMPLATES…</div>
                ) : templates.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '52px 28px', borderRadius: 16, border: '1.5px dashed var(--hv-line-2)', background: 'var(--hv-page-2)' }}>
                        <div style={{ fontFamily: FONT_SERIF, fontSize: 24, letterSpacing: '-0.01em' }}>The template catalog is not available yet</div>
                        <p style={{ margin: 0, maxWidth: 360, textAlign: 'center', fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>Start from a blank page instead: describe what you want in the box above.</p>
                    </div>
                ) : (
                    <div className="hv-cols-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
                        {shown.map((t) => <TemplateCard key={t.id} t={t} busyId={busyId} onUse={onUse} ring={ringId === t.id} />)}
                    </div>
                )}

                {templates.length > PAGE && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 34 }}>
                        <Link
                            to={byCat ? `/templates?category=${encodeURIComponent(cat)}` : '/templates'}
                            data-track="templates_see_all"
                            data-track-label={byCat ? cat : 'All'}
                            className="hv-h-card-bd"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 9, height: 48, padding: '0 24px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}
                        >
                            {byCat ? `See all ${filtered.length} ${cat} templates` : `See all ${templates.length} templates`}
                            <TplArrow />
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
};
